import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { BrandLockup } from "./BrandMark";
import ModelPicker from "./ModelPicker";
import ThemePicker from "./ThemePicker";
import { useAuth } from "../context/AuthContext";

function initials(username) {
  if (!username) return "?";
  return username.trim().slice(0, 2).toUpperCase();
}

function useDismiss(open, setOpen) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, setOpen]);
  return ref;
}

const SETTINGS = [
  { to: "/users", label: "Users", hint: "Sign-ins and role assignment", icon: "M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1M9 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm13 9v-1a4 4 0 0 0-3-3.9M16 4.1a3 3 0 0 1 0 5.8" },
  { to: "/roles", label: "Roles", hint: "Table, column and document access", icon: "M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" },
  { to: "/table-catalog", label: "Table Catalog", hint: "Table and column descriptions", icon: "M4 5h16v14H4zM4 10h16M10 10v9" },
  { to: "/documents", label: "Documents", hint: "Knowledge base uploads", icon: "M14 3H6v18h12V7zM14 3v4h4M9 13h6M9 17h6" },
  { to: "/ai-models", label: "AI models", hint: "Claude, ChatGPT, NVIDIA and your own", icon: "M12 2l2.4 5.6L20 10l-5.6 2.4L12 18l-2.4-5.6L4 10l5.6-2.4zM19 16l.9 2.1L22 19l-2.1.9L19 22l-.9-2.1L16 19l2.1-.9z" },
];

function Glyph({ d }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
  );
}

function SettingsMenu() {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, setOpen);
  const { pathname } = useLocation();
  const active = SETTINGS.some((s) => pathname.startsWith(s.to));

  return (
    <div className="settings-menu" ref={ref}>
      <button type="button" className={"settings-btn" + (active || open ? " active" : "")} onClick={() => setOpen((v) => !v)}
              aria-haspopup="menu" aria-expanded={open} title="Settings">
        <Glyph d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm7.4-3a7.4 7.4 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a7.6 7.6 0 0 0-2-1.2L14.5 3h-5l-.4 2.6a7.6 7.6 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6a7.4 7.4 0 0 0 0 2.4l-2 1.6 2 3.4 2.4-1a7.6 7.6 0 0 0 2 1.2l.4 2.6h5l.4-2.6a7.6 7.6 0 0 0 2-1.2l2.4 1 2-3.4-2-1.6c.1-.4.1-.8.1-1.2z" />
        <span>Settings</span>
      </button>
      {open && (
        <div className="settings-dropdown" role="menu">
          <div className="settings-label">Workspace settings</div>
          {SETTINGS.map((s) => (
            <NavLink key={s.to} to={s.to} role="menuitem" onClick={() => setOpen(false)}
                     className={({ isActive }) => "settings-item" + (isActive ? " active" : "")}>
              <span className="settings-ic"><Glyph d={s.icon} /></span>
              <span className="settings-txt"><strong>{s.label}</strong><small>{s.hint}</small></span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, setOpen);

  const roles = user?.roles?.length ? user.roles.join(", ") : user?.role;
  const role = user?.isAdmin ? "Admin" : roles || "No role assigned";

  return (
    <div className="profile-menu" ref={ref}>
      <button className="profile-avatar" onClick={() => setOpen((v) => !v)} title={user?.username}>
        {initials(user?.username)}
      </button>
      {open && (
        <div className="profile-dropdown">
          <div className="profile-who">
            <div className="profile-name">{user?.username}</div>
            <div className="profile-role">{role}</div>
            {user?.workspace && <div className="profile-role">Workspace: {user.workspace.name} ({user.workspace.slug})</div>}
          </div>
          <div className="profile-section">
            <div className="profile-section-label">Theme</div>
            <ThemePicker />
          </div>
          <button className="profile-signout" onClick={logout}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function TopNav() {
  const { user } = useAuth();

  const modules = [
    { to: "/", label: "Chat", end: true },
    { to: "/dashboards", label: "Dashboards" },
  ];
  if (user?.isAdmin) {
    modules.push({ to: "/models", label: "Models" });
    modules.push({ to: "/jobs", label: "Jobs" });
    modules.push({ to: "/agents", label: "Agents" });
    modules.push({ to: "/connections", label: "Connections" });
  }

  return (
    <nav className="topnav">
      <span className="topnav-brand" title="QLens by Aireido Tech Pvt Ltd">
        <BrandLockup size={28} />
      </span>
      <div className="topnav-links">
        {modules.map((m) => (
          <NavLink key={m.to} to={m.to} end={m.end} className={({ isActive }) => "topnav-link" + (isActive ? " active" : "")}>
            {m.label}
          </NavLink>
        ))}
      </div>
      {user?.workspace && (
        <span className="ws-chip" title={`Workspace: ${user.workspace.slug}`}>
          <span className="ws-dot" />{user.workspace.name}
          {!user.workspace.isDefault && <span className="ws-plan">{user.workspace.planLabel}</span>}
        </span>
      )}
      <ModelPicker />
      {user?.isAdmin && <SettingsMenu />}
      <ProfileMenu />
    </nav>
  );
}
